import React, { useCallback, useRef } from 'react';
import type {
  AccessibilityProps,
  StyleProp,
  ViewProps,
  ViewStyle,
} from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { usePressableState } from '../../hooks';
import { webOnlyStyles } from '../../utils';
import { Icon } from '../Icon';
import { BodyText } from '../Text';

// ────────────────────────────────────────────────────────────────────────────────

export const Checkbox = (props: CheckboxProps) => {
  const {
    testID,
    checked,
    label,
    accessibilityLabel,
    disabled = false,
    bold,
    style,
    children,
    onChange,
    onBlur,
  } = props;

  const handleChange = useCallback(() => {
    if (disabled) return;
    onChange(!checked);
  }, [disabled, onChange, checked]);

  // ────────────────────────────────────────────────────────────────────

  return (
    <View style={[styles.wrapper, style]} testID={testID}>
      <CheckboxToggle
        checked={checked}
        onPress={handleChange}
        onBlur={onBlur}
        disabled={disabled}
        accessibilityLabel={accessibilityLabel ?? label}
      />

      {children ? (
        <CheckboxChildren onPress={handleChange} disabled={disabled}>
          {children}
        </CheckboxChildren>
      ) : label ? (
        <CheckboxLabel
          label={label}
          onPress={handleChange}
          disabled={disabled}
          bold={bold}
        />
      ) : null}
    </View>
  );
};

// ─── SUBCOMPONENTS ──────────────────────────────────────────────────────────────

const CheckboxToggle = (props: CheckboxToggleProps) => {
  const {
    checked,
    disabled = false,
    accessibilityLabel = 'Toggle checkbox',
    onPress,
    onBlur,
    testID,
  } = props;

  const toggleRef = useRef(null);
  const { isHovered } = usePressableState(toggleRef);

  // ────────────────────────────────────────────────────────────────────

  const checkboxStates = { hovered: isHovered, checked, disabled };
  const iconColor = getCheckboxIconColor(checkboxStates);
  const style = [styles.checkbox, getCheckboxToggleStyles(checkboxStates)];

  // ────────────────────────────────────────────────────────────────────

  return (
    <Pressable
      testID={testID}
      ref={toggleRef}
      style={style}
      disabled={disabled}
      accessibilityRole="checkbox"
      accessibilityLabel={accessibilityLabel}
      {...{ accessibilityChecked: checked }}
      onBlur={onBlur}
      onPress={onPress}
    >
      {checked ? (
        <Icon
          name="IconCheck"
          color={iconColor}
          width={CHECKBOX_ICON_SIZE}
          height={CHECKBOX_ICON_SIZE}
        />
      ) : null}
    </Pressable>
  );
};

const CheckboxLabel = (props: CheckboxLabelProps) => {
  const { label, disabled, bold, onPress } = props;

  return (
    <CheckboxChildren disabled={disabled} onPress={onPress}>
      <BodyText size={4} bold={bold}>
        {label}
      </BodyText>
    </CheckboxChildren>
  );
};

const CheckboxChildren = (props: CheckboxChildrenProps) => {
  const { children, disabled, onPress } = props;

  const labelRef = useRef(null);
  const { isHovered } = usePressableState(labelRef);

  // ────────────────────────────────────────────────────────────────────

  const pressableStyle = [
    disabled && { color: CHECKBOX_DISABLED_COLOR },
    !disabled && isHovered && styles.labelHovered,
    webOnlyStyles({ userSelect: 'none' }),
  ];

  // ────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.labelWrapper}>
      <Pressable
        accessibilityRole="button"
        ref={labelRef}
        onPress={onPress}
        disabled={disabled}
        style={pressableStyle}
      >
        {children}
      </Pressable>
    </View>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type CheckboxProps = Readonly<{
  checked: boolean;
  disabled?: boolean;
  onChange: (value?: boolean) => void;
  onBlur?: () => void;
  label?: string;
  testID?: string;
  style?: StyleProp<ViewStyle>;
  bold?: boolean;
  children?: ViewProps['children'];
}> &
  Pick<AccessibilityProps, 'accessibilityLabel'>;

type CheckboxToggleProps = Readonly<{
  onPress: () => void;
}> &
  Pick<
    CheckboxProps,
    'accessibilityLabel' | 'checked' | 'disabled' | 'onBlur' | 'testID'
  >;

type CheckboxLabelProps = Readonly<{
  bold?: boolean;
  onPress: () => void;
}> &
  Pick<CheckboxProps, 'label' | 'disabled'>;

type CheckboxChildrenProps = Readonly<{
  children?: ViewProps['children'];
  onPress: () => void;
}> &
  Pick<CheckboxProps, 'disabled'>;

type CheckboxStates = Readonly<{
  hovered: boolean;
}> &
  Pick<CheckboxProps, 'checked' | 'disabled'>;

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────

const CHECKBOX_SIZE = 24;
const CHECKBOX_BG_COLOR = theme.colors.WHITE;
const CHECKBOX_COLOR = theme.colors.KALE;
const CHECKBOX_DISABLED_COLOR = theme.colors.GRAY;
const CHECKBOX_HOVER_COLOR = theme.colors.OPACITY.GREEN_2.LIGHTEST;
const CHECKBOX_ICON_COLOR = CHECKBOX_BG_COLOR;
const CHECKBOX_ICON_SIZE = 16;

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  labelWrapper: {
    paddingLeft: theme.spacing['4'],
    flexShrink: 1,
  },
  labelHovered: {
    opacity: 0.7,
  },
  checkbox: {
    width: CHECKBOX_SIZE,
    height: CHECKBOX_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderRadius: theme.radius.small,
  },
  checkboxIdle: {
    borderColor: CHECKBOX_COLOR,
  },
  checkboxChecked: {
    backgroundColor: CHECKBOX_COLOR,
    borderColor: CHECKBOX_COLOR,
  },
  checkboxDisabled: {
    borderColor: CHECKBOX_DISABLED_COLOR,
  },
  checkboxHover: {
    backgroundColor: CHECKBOX_HOVER_COLOR,
    borderColor: CHECKBOX_COLOR,
  },
});

// ─── UTILS ──────────────────────────────────────────────────────────────────────

const getCheckboxToggleStyles = (states: CheckboxStates) => {
  if (states.disabled)
    return [styles.checkboxDisabled, webOnlyStyles({ cursor: 'not-allowed' })];

  if (states.hovered) return styles.checkboxHover;

  if (states.checked) return styles.checkboxChecked;

  return styles.checkboxIdle;
};

const getCheckboxIconColor = (states: CheckboxStates) => {
  if (states.disabled) return CHECKBOX_DISABLED_COLOR;

  if (states.hovered) return CHECKBOX_COLOR;

  return CHECKBOX_ICON_COLOR;
};
